import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { TESTIMONIALS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const [items, setItems] = useState(TESTIMONIALS);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const t = items[idx];
  const go = (d) => setIdx((v) => (v + d + items.length) % items.length);

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(({ data }) => setItems([...TESTIMONIALS, ...data.map((r) => ({ ...r, photo: "" }))]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => setIdx((v) => (v + 1) % items.length), 8000);
    return () => clearTimeout(id);
  }, [paused, idx, items.length]);

  return (
    <section
      data-testid="testimonials-section"
      className="py-24 md:py-36"
      style={{ backgroundColor: "var(--mocha-brown)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <Reveal>
          <h2
            data-testid="testimonials-heading"
            className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-12"
            style={{ color: "#FBF6F3" }}
          >
            What Our Clients Are Saying
          </h2>
          <Quote
            size={56}
            style={{ color: "var(--accent-gold)" }}
            className="mb-8"
          />
        </Reveal>
        <div className="min-h-[220px] md:min-h-[200px] flex items-center">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <p
                className="font-serif italic text-2xl md:text-4xl leading-snug tracking-tight"
                style={{ color: "#FBF6F3" }}
              >
                “{t.quote}”
              </p>
              <footer className="mt-8 flex items-center gap-4">
                <span
                  className="grid place-items-center w-14 h-14 rounded-full overflow-hidden shrink-0"
                  style={{
                    backgroundColor: "var(--rose-mocha)",
                    border: "1px solid var(--accent-gold)",
                  }}
                >
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={t.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span
                      className="font-serif text-lg"
                      style={{ color: "#FBF6F3" }}
                    >
                      {t.name
                        .split(" ")
                        .map((w) => w[0])
                        .join("")
                        .slice(0, 2)}
                    </span>
                  )}
                </span>
                <div>
                  <p
                    className="text-base font-medium"
                    style={{ color: "var(--accent-gold-soft)" }}
                  >
                    {t.name}
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: "rgba(251,246,243,0.6)" }}
                  >
                    {t.role}
                  </p>
                </div>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-4 mt-10">
          <button
            data-testid="testimonial-prev"
            onClick={() => go(-1)}
            className="grid place-items-center w-12 h-12 rounded-full transition-colors duration-300"
            style={{ border: "1px solid rgba(251,246,243,0.3)", color: "#FBF6F3" }}
          >
            <ArrowLeft size={18} />
          </button>
          <button
            data-testid="testimonial-next"
            onClick={() => go(1)}
            className="grid place-items-center w-12 h-12 rounded-full transition-colors duration-300"
            style={{ border: "1px solid rgba(251,246,243,0.3)", color: "#FBF6F3" }}
          >
            <ArrowRight size={18} />
          </button>
          <span className="ml-3 text-sm" style={{ color: "rgba(251,246,243,0.6)" }}>
            {String(idx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}
